import React, { useCallback, useImperativeHandle, useState } from "react";
import { Pie, PieChart, Cell, Tooltip, Sector } from "recharts";
import {
  Box,
  styled,
  Typography,
  useMediaQuery,
  Theme,
  useTheme,
} from "@mui/material";
import AutoSizer from "react-virtualized-auto-sizer";
import { useTranslation } from "react-i18next";
import { toCurrency } from "@helpers";
import { DonutChartLegend } from "../DonutChartLegend/DonutChartLegend";
import { DonutChartLabel } from "./DonutChartLabel";
import { DonutChartTooltip } from "./DonutChartTooltip";
import { DonutChartData } from "./types";
import { useChartData } from "./useChartData";

const renderActiveShape = (props: any, theme: Theme) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    color,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    midAngle,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + 5 * cos;
  const sy = cy + 5 * sin;

  return (
    <g>
      <Sector
        cx={sx}
        cy={sy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        strokeWidth={5}
        fill={color}
        stroke={theme.palette.background.default}
        style={{ cursor: "pointer" }}
      />
      {/*this is to prevent twitching effect when moving slowly between sectors */}
      <Sector
        cx={sx}
        cy={sy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        strokeWidth={10}
        fill="transparent"
        stroke="transparent"
        style={{ cursor: "pointer" }}
      />
    </g>
  );
};

export const DonutChartContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(10),

  [theme.breakpoints.down(950)]: {
    flexDirection: "column",
  },
}));

export type DonutChartProps = {
  data: DonutChartData[];
  displayLegend?: boolean;
  displayTotal?: boolean;
  colors?: string[];
  onLegendClick?: (id: string | null) => void;
  onSectionClick?: (id: string | string[]) => void;
};

export const DonutChart = ({
  ref,
  data,
  displayLegend = false,
  displayTotal = false,
  onLegendClick,
  onSectionClick,
  colors,
}: DonutChartProps & {
  ref?: React.Ref<{ resetActiveItem: () => void }>;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLessSm = useMediaQuery((theme: Theme) => theme.breakpoints.down(750));
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const onlyOneItem = data.length === 1;

  const { chartData, totalValue, totalAbsValue } = useChartData(data, colors);

  const resetActiveItem = useCallback(() => {
    setActiveItem(null);
    onLegendClick?.(null);
  }, [onLegendClick]);

  useImperativeHandle(
    ref,
    () => ({
      resetActiveItem,
    }),
    [resetActiveItem]
  );

  const handleSectionClick = (id: string) => {
    if (onSectionClick) {
      const clickedItem = chartData.find((item) => item.id === id);
      if (!clickedItem) return;

      onSectionClick(
        clickedItem?.children
          ? clickedItem.children.map((child) => child.id)
          : clickedItem.id
      );
      handleLegendClick(id, false);
    } else {
      handleLegendClick(id);
    }
  };

  const handleLegendClick = (
    id: string,
    shouldTriggerOnLegendClick: boolean = true
  ) => {
    if (!displayLegend) return;
    if (activeItem === id) {
      setActiveItem(null);
      if (shouldTriggerOnLegendClick) onLegendClick?.(null);
    } else {
      setActiveItem(id);
      if (shouldTriggerOnLegendClick) onLegendClick?.(id);
    }
  };

  const activeItemIndex = chartData.findIndex(
    (item) =>
      item.id === activeItem ||
      item.children?.some((child) => child.id === activeItem)
  );

  return (
    <DonutChartContainer>
      <DonutChartLegend
        data={data}
        displayLegend={displayLegend}
        onLegendClick={handleLegendClick}
        activeLegend={activeItem}
      />
      <Box
        display="flex"
        width="100%"
        flexDirection="column"
        gap={isLessSm ? 3 : 6}
        height={displayTotal ? 430 : 360}
      >
        {displayTotal && (
          <Box textAlign="center">
            <Typography fontSize="18px" lineHeight="24px">
              {toCurrency(
                chartData[0]?.id === "noData" ? 0 : totalValue,
                "EUR"
              )}
            </Typography>
            <Typography fontSize="12px" lineHeight="18px" color="textSecondary">
              {t("domains.donutChart.total")}
            </Typography>
          </Box>
        )}
        <Box height={`calc(100% - ${displayTotal ? 50 : 0}px)`} width="100%">
          <AutoSizer>
            {({ width, height }) => {
              const lowestNumber = width < height ? width : height;

              return (
                <PieChart
                  width={width}
                  height={height}
                  margin={{ top: 10, bottom: 10 }}
                >
                  <Pie
                    activeIndex={onlyOneItem ? undefined : activeItemIndex}
                    activeShape={(props: unknown) =>
                      renderActiveShape(props, theme)
                    }
                    data={chartData}
                    innerRadius={lowestNumber / 6}
                    outerRadius={lowestNumber / 2 - 5}
                    dataKey="value"
                    labelLine={false}
                    label={DonutChartLabel}
                    isAnimationActive={false}
                    onClick={(props) => handleSectionClick(props.id)}
                  >
                    {chartData.map((item) => (
                      <Cell
                        key={item.id}
                        fill={item.color}
                        strokeWidth={5}
                        stroke={theme.palette.background.default}
                        style={{
                          cursor: displayLegend ? "pointer" : "default",
                        }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    active
                    content={<DonutChartTooltip totalValue={totalAbsValue} />}
                  />
                </PieChart>
              );
            }}
          </AutoSizer>
        </Box>
      </Box>
    </DonutChartContainer>
  );
};
